import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from 'ui';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { MeetingJoineeStatus, MeetingStatus, MeetingType } from 'types';
import { useMutation } from 'react-query';
import UnAuthorizedParticipantScreen from 'src/components/interviews/UnAuthorizedParticipantScreen';
import InterviewEndedScreen from 'src/components/interviews/InterviewEndedScreen';
import InterviewNotStartedScreen from 'src/components/interviews/InterviewNotStartedScreen';
import LiveInterviewScreen from 'src/components/interviews/LiveInterviewScreen';
import { createMeetingLog, createParticipantToken } from 'src/apis/meeting';
import { useGetMeetingByIdQuery } from 'src/hooks/meeting.hooks';
import { useMeetingContext } from 'src/context/MeetingProvider';
import Seo from 'src/components/Seo';
import seoConfig from '../seo.json';
import { get } from 'lodash';
import InterviewLayout from 'src/components/interviews/InterviewLayout';
import AIInterviewLayout from 'src/components/interviews/AIInterviewLayout';
import LiveAIInterviewScreen from 'src/components/interviews/LiveAIInterviewScreen';

const LiveInterview = () => {
	const { user } = useUserContext();
	const { id } = useParams();
	const toast = useToast();
	const navigate = useNavigate();
	const { setMeeting } = useMeetingContext();

	const seoInfo = seoConfig['live-interview-js'];

	const [hasInterviewStarted, setHasInterviewStarted] = useState(false);
	const [hasInterviewEnded, setHasInterviewEnded] = useState<boolean>(false);
	const [notParticipant, setNotParticipant] = useState<boolean>(false);
	const [participantToken, setParticipantToken] = useState<string>('');
	const [isHost, setIsHost] = useState(false);
	const [attendanceLogged, setAttendanceLogged] = useState(false);
	const [done, setDone] = useState(false);

	const createParticipantTokenFn = useMutation({
		mutationKey: 'createParticipantToken',
		mutationFn: createParticipantToken,
		onSuccess(data) {
			if (data?.token) setParticipantToken(data.token);
			setDone(true);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const meetingResponse = useGetMeetingByIdQuery(
		id,
		data => {
			if (!data.meeting) {
				// navigate('/dashboard');
				navigate('/my-applications');
			}
			setMeeting(data.meeting);
			const loggedInUserId = user?._id;
			if (data.meeting?.host._id === loggedInUserId) {
				setIsHost(true);
			}
			let pToken = data.meeting?.participantTokens?.find(pT => pT.participant._id === loggedInUserId);
			if (pToken && pToken.token) {
				if (!done) handleCreateParticipantToken();
				else setParticipantToken(pToken.token);
			} else if (!user) {
				setNotParticipant(true);
			} else if ([data.meeting?.participant._id, data.meeting?.host._id].includes(user._id) && !done) {
				handleCreateParticipantToken();
			} else {
				setNotParticipant(true);
			}
			if (data.meeting?.meetingStatus === MeetingStatus.SCHEDULED) {
				setHasInterviewStarted(false);
			} else if (data.meeting?.meetingStatus === MeetingStatus.STARTED) {
				setHasInterviewStarted(true);
			} else {
				setHasInterviewEnded(true);
			}
		},
		() => {
			setNotParticipant(true);
		},
	);

	const addUserMeetingLog = useMutation({
		mutationKey: 'createMeetingLog',
		mutationFn: createMeetingLog,
	});

	const handleCreateParticipantToken = () => {
		if (id) createParticipantTokenFn.mutate(id);
	};

	useEffect(() => {
		if (user?._id && meetingResponse.data && !attendanceLogged) {
			addUserMeetingLog.mutate({
				meetingId: meetingResponse.data.meeting?.dyteMeetingId || '',
				participant: user._id,
				status: MeetingJoineeStatus.JOINED,
			});
			setAttendanceLogged(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, meetingResponse.data]);

	useEffect(() => {
		if (meetingResponse.data?.meeting) setMeeting(meetingResponse.data.meeting);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meetingResponse.data]);

	if (meetingResponse.isLoading || createParticipantTokenFn.isLoading) return <Loader />;

	if (notParticipant) {
		return (
			<div className="h-screen w-screen dark:bg-[#1f2023]">
				<UnAuthorizedParticipantScreen />
			</div>
		);
	} else if (meetingResponse.data) {
		if (hasInterviewEnded) {
			return (
				<div className="h-screen w-screen dark:bg-[#1f2023]">
					<InterviewEndedScreen />
				</div>
			);
		} else if (hasInterviewStarted) {
			return (
				<>
					<Seo title={seoInfo.title} description={seoInfo.description} />
					<div className="h-screen w-full dark:bg-[#1f2023]">
						{meetingResponse.data.meeting.isAIInterview ||
						meetingResponse.data.meeting.meetingType === MeetingType.Assessment ? (
							<AIInterviewLayout>
								<LiveAIInterviewScreen isHost={isHost} />
							</AIInterviewLayout>
						) : (
							<InterviewLayout token={participantToken}>
								<LiveInterviewScreen isHost={isHost} />
							</InterviewLayout>
						)}
					</div>
				</>
			);
		} else {
			return (
				<div className="h-screen w-screen dark:bg-[#1f2023]">
					<InterviewNotStartedScreen meeting={meetingResponse.data.meeting} isHost={isHost} />
				</div>
			);
		}
	} else return <div />;
};

export default LiveInterview;
